import React, { useEffect, useState } from "react";
import { Box, Grid, Skeleton, Stack, Typography } from "@mui/material";
import {
  SignalBar,
  SectionPanel,
  ClinicalDataGrid,
  StatusPill,
  fmtMoney,
  tokens,
} from "../../shared/clinical-design";
import * as svc from "../services/chargeCaptureService";

const PROVIDER_STATUS = {
  ok:       { tone: "ok",   label: "On target" },
  coaching: { tone: "warn", label: "Coaching" },
  review:   { tone: "crit", label: "Review" },
};

/**
 * Métricas mensuales del módulo (doc §5.1): capture rate vs target, HCCs
 * adicionales, impacto en revenue y desempeño por provider.
 * Fuente futura: GET /api/charges/metrics?month=...
 */
export default function MetricsPanel() {
  const [metrics, setMetrics] = useState(null);

  useEffect(() => {
    svc.getMetrics().then(setMetrics);
  }, []);

  if (!metrics) return <Skeleton variant="rounded" height={320} />;

  return (
    <Stack spacing={1.5}>
      <SignalBar
        signals={[
          {
            label: `Capture rate (${metrics.monthLabel})`,
            value: `${metrics.captureRate}%`,
            trend: `target ${metrics.captureRateTarget}%`,
            tone: metrics.captureRate >= metrics.captureRateTarget ? "ok" : "warn",
          },
          { label: "HCCs captured", value: metrics.hccsAdditionalCaptured, tone: "ok" },
          { label: "Specificity fixes", value: metrics.specificityImprovements, tone: "info" },
          { label: "Revenue impact", value: fmtMoney(metrics.revenueImpact), tone: "ok" },
          { label: "Missing >24h", value: metrics.alerts.missingChargesOver24h, tone: metrics.alerts.missingChargesOver24h > 0 ? "crit" : "ok" },
          { label: "Open queries", value: metrics.alerts.queriesPendingResponse, tone: metrics.alerts.queriesPendingResponse > 0 ? "warn" : "ok" },
        ]}
      />
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 12, md: 7 }}>
          <SectionPanel title="Capture rate by provider" contentSx={{ p: 0 }}>
            <ClinicalDataGrid
              rows={metrics.byProvider}
              rowKey={(r) => r.name}
              maxHeight={420}
              sx={{ border: 0 }}
              columns={[
                { key: "name", header: "Provider" },
                {
                  key: "rate",
                  header: "Capture rate",
                  align: "right",
                  width: 130,
                  mono: true,
                  render: (r) => `${r.rate}%`,
                },
                {
                  key: "bar",
                  header: "",
                  width: 220,
                  render: (r) => (
                    <Box sx={{ width: 200, height: 8, bgcolor: tokens.mutedBg, borderRadius: "2px" }}>
                      <Box
                        sx={{
                          width: `${r.rate}%`,
                          height: "100%",
                          borderRadius: "2px",
                          bgcolor: r.rate >= 98 ? tokens.ok : r.rate >= 94 ? tokens.warn : tokens.crit,
                        }}
                      />
                    </Box>
                  ),
                },
                {
                  key: "status",
                  header: "Status",
                  width: 120,
                  render: (r) => {
                    const st = PROVIDER_STATUS[r.status] || PROVIDER_STATUS.ok;
                    return <StatusPill tone={st.tone} label={st.label} dense />;
                  },
                },
              ]}
            />
          </SectionPanel>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <SectionPanel title="Open alerts" sx={{ height: "100%" }}>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontSize: 13 }}>Specificity improvements pending review</Typography>
                <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 15, fontWeight: 600, color: tokens.warn }}>
                  {metrics.alerts.specificityPending}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontSize: 13 }}>Missing charges older than 24h</Typography>
                <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 15, fontWeight: 600, color: tokens.crit }}>
                  {metrics.alerts.missingChargesOver24h}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontSize: 13 }}>Provider queries pending response</Typography>
                <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 15, fontWeight: 600, color: tokens.warn }}>
                  {metrics.alerts.queriesPendingResponse}
                </Typography>
              </Stack>
            </Stack>
          </SectionPanel>
        </Grid>
      </Grid>
    </Stack>
  );
}
